import React, { useState } from 'react';

import SummaryCard from './SummaryCard';

function FamilyIncomeExpenses({
    monthlyIncome,
    monthlyExpenses,
    netMonthlySavings,
    savingsRate,
    formatCurrency,
    familyMembers,
    otherIncomeSources,
    savingsAllocation
}) {
    const [viewMode, setViewMode] = useState('yearly'); // 'yearly' or 'monthly'

    // Calculate values based on view mode
    const income = viewMode === 'yearly' ? monthlyIncome * 12 : monthlyIncome;
    const expenses = viewMode === 'yearly' ? monthlyExpenses * 12 : monthlyExpenses;
    const savingsAmount = viewMode === 'yearly' ? netMonthlySavings * 12 : netMonthlySavings;
    const savingsRateDisplay = savingsRate > 0 ? Math.round(savingsRate) : 0;
    const incomeUnit = viewMode === 'yearly' ? 'Yr' : 'Mth';
    const incomeLabel = viewMode === 'yearly' ? 'Yearly' : 'Monthly';

    // Helper for member role display
    const getRoleDisplay = (role, dependent) => {
        if (role === 'You') return 'You';
        if (role === 'Wife') return 'Wife';
        if (role === 'Husband') return 'Husband';
        if (dependent) return `${role} (Dependent)`;
        return role;
    };

    // Helper for age display
    const getAgeDisplay = (age, dependent) => {
        if (!age) return '';
        return `${age} Yrs${dependent ? ' (Dependent)' : ''}`;
    };

    return (
        <SummaryCard title="Family Income & Expenses">
            {/* Toggle switcher */}
            <div className="flex justify-end mb-3">
                <div className="flex bg-gray-100 rounded-full">
                    <button
                        className={`px-4 py-1.5 content-text text-sm font-medium rounded-full transition-all duration-200 ${viewMode === 'yearly'
                            ? 'text-white bg-purple-600 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                            }`}
                        onClick={() => setViewMode('yearly')}
                    >
                        Yearly
                    </button>
                    <button
                        className={`px-4 py-1.5 content-text text-sm font-medium rounded-full transition-all duration-200 ${viewMode === 'monthly'
                            ? 'text-white bg-purple-600 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                            }`}
                        onClick={() => setViewMode('monthly')}
                    >
                        Monthly
                    </button>
                </div>
            </div>

            {/* Your Family */}
            <div className="mb-3">
                <div className="flex justify-between items-center mb-3">
                    <h4 className="content-text text-gray-800 font-medium">Your Family</h4>
                    <span className="content-text text-gray-500">{familyMembers.length} members</span>
                </div>
                <div className="space-y-3">
                    {familyMembers.map((member, idx) => (
                        <div key={member.name} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                {member.avatar ? (
                                    <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                    <span className="content-text font-semibold text-purple-700">
                                        {member.name[0]}
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="content-text font-medium text-gray-800">{member.name}</div>
                                <div className="content-text text-gray-500">
                                    {getAgeDisplay(member.age, member.dependent)}
                                </div>
                            </div>
                            <div className="text-right flex flex-col items-end">
                                <span className="content-text text-gray-500 text-sm">
                                    {getRoleDisplay(member.role, member.dependent)}
                                </span>
                                <div className="content-text font-medium text-gray-800">
                                    {member.income ?
                                        `₹ ${viewMode === 'yearly' ? (member.income * 12 / 100000).toFixed(1) : (member.income / 100000).toFixed(1)} Lakhs/${incomeUnit}`
                                        : '--'
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Border separator */}
            <div className="border-b border-gray-300 my-3"></div>

            {/* Other Income Sources */}
            <div className="mb-3">
                <h4 className="content-text text-gray-800 font-medium mb-3">Other Income Sources</h4>
                <div className="space-y-3">
                    {otherIncomeSources.map((src, idx) => (
                        <div key={src.label} className="flex justify-between items-center">
                            <span className="content-text text-gray-500">{src.label}</span>
                            <span className="content-text font-medium text-gray-800">
                                ₹ {src.value ? `${(src.value / 100000).toFixed(1)} Lakh/${incomeUnit}` : '0'}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Border separator */}
            <div className="border-b border-gray-300 my-3"></div>

            {/* Savings Allocation */}
            <div className="mb-3">
                <h4 className="content-text text-gray-800 font-medium mb-3">Savings Allocation</h4>
                <div className="space-y-3">
                    {savingsAllocation && savingsAllocation.map((item, idx) => (
                        <div key={item.label} className="flex justify-between items-center">
                            <span className="content-text text-gray-500">{item.label}</span>
                            <span className="content-text font-medium text-gray-800">
                                ₹ {(item.value / 1000).toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Border separator */}
            <div className="border-b border-gray-300 my-3"></div>

            {/* Income and Expenses Summary */}
            <div className="space-y-3 mb-3">
                <div className="flex justify-between items-center">
                    <h4 className="content-text text-gray-800 font-medium">{incomeLabel} Family Income</h4>
                    <p className="text-xl font-bold text-gray-800">{formatCurrency(income)}</p>
                </div>
                <div className="flex justify-between items-center">
                    <h4 className="content-text text-gray-800 font-medium">{incomeLabel} Family Expenses</h4>
                    <p className="text-xl font-bold text-red-500">{formatCurrency(expenses)}</p>
                </div>
            </div>

            {/* Family Savings Rate Card */}
            <div className="border-2 border-gray-800 rounded-lg p-4 mb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-green-500 text-lg">↗</span>
                        <span className="content-text font-medium text-gray-800">Family Savings</span>
                    </div>
                    <span className="text-xl font-bold text-green-600">{formatCurrency(savingsAmount)}</span>
                </div>
            </div>

            {/* Percentage text outside the card */}
            <p className="content-text text-gray-600 text-center">
                You are <strong>saving {savingsRateDisplay}%</strong> of your {viewMode === 'yearly' ? 'annual' : 'monthly'} income!
            </p>
        </SummaryCard>
    );
}

export default FamilyIncomeExpenses;
